import { useOutletContext } from 'react-router';
import { Card, StatusBadge, type StatusTone } from '@afrilink/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  adminCustomerService,
  type AdminCustomerLink,
  type LifecycleStage,
} from '@/domains/customers/services/adminCustomerService';
import type { CustomerDetailContext } from './customerDetailTypes';
import { formatZAR } from '@/shared/utils/format';

const STAGE_TONE: Record<LifecycleStage, StatusTone> = {
  lead: 'info',
  active: 'active',
  suspended: 'overdue',
  cancellation_pending: 'pending',
  churned: 'draft',
};
const STAGE_LABEL: Record<LifecycleStage, string> = {
  lead: 'Lead',
  active: 'Active',
  suspended: 'Suspended',
  cancellation_pending: 'Cancelling',
  churned: 'Churned',
};

const LIFECYCLE_ORDER: LifecycleStage[] = [
  'lead',
  'active',
  'suspended',
  'churned',
];

function monthlyAmountFromLink(link: AdminCustomerLink): number {
  const amt = link.service_plan_recurring_amount ?? 0;
  switch (link.service_plan_billing_cycle) {
    case 'monthly':
      return amt;
    case 'annual':
      return amt / 12;
    case 'biennial':
      return amt / 24;
    default:
      return amt;
  }
}

export default function CustomerOverviewTab() {
  const { customer } = useOutletContext<CustomerDetailContext>();
  const [links, setLinks] = useState<AdminCustomerLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminCustomerService
      .listLinks(customer.customer_id)
      .then((data) => {
        if (!cancelled) {
          setLinks(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer.customer_id]);

  const mrr = useMemo(
    () => links.reduce((sum, l) => sum + monthlyAmountFromLink(l), 0),
    [links]
  );

  const stageIndex = LIFECYCLE_ORDER.indexOf(
    customer.lifecycle_stage === 'cancellation_pending'
      ? 'suspended'
      : customer.lifecycle_stage
  );

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Profile */}
      <Card interactive={false} className="lg:col-span-2">
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-foreground">Profile</h3>
        </header>
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-5 text-sm sm:grid-cols-2">
          <Field label="Full name" value={customer.full_name} />
          <Field label="Email" value={customer.email ?? '—'} />
          <Field label="Phone" value={customer.phone ?? '—'} />
          <Field label="Company" value={customer.company ?? '—'} />
          <Field
            label="Registration #"
            value={customer.registration_number ?? '—'}
          />
          <Field label="VAT #" value={customer.vat_number ?? '—'} />
          <div className="sm:col-span-2">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Billing address
            </p>
            <p className="text-foreground">
              {[
                customer.billing_address_line1,
                customer.billing_address_line2,
                customer.billing_address_city,
                customer.billing_address_postal_code,
                customer.billing_address_country,
              ]
                .filter(Boolean)
                .join(', ') || '—'}
            </p>
          </div>
          {customer.billing_email && (
            <Field label="Billing email" value={customer.billing_email} />
          )}
        </div>
      </Card>

      {/* Lifecycle + quick stats stack */}
      <div className="space-y-6">
        <Card interactive={false}>
          <header className="border-b border-border px-6 py-3">
            <h3 className="text-sm font-semibold text-foreground">Lifecycle</h3>
          </header>
          <div className="space-y-3 px-6 py-5">
            <StatusBadge tone={STAGE_TONE[customer.lifecycle_stage]} dot>
              {STAGE_LABEL[customer.lifecycle_stage]}
            </StatusBadge>
            <div className="flex items-center gap-2 pt-2">
              {LIFECYCLE_ORDER.map((stage, i) => (
                <div key={stage} className="flex items-center gap-2">
                  <span
                    className={
                      i <= stageIndex
                        ? 'h-2.5 w-2.5 rounded-full bg-primary'
                        : 'h-2.5 w-2.5 rounded-full border border-border'
                    }
                    aria-label={STAGE_LABEL[stage]}
                  />
                  {i < LIFECYCLE_ORDER.length - 1 && (
                    <span className="h-px w-4 bg-border" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Customer since{' '}
              {new Date(customer.created_at).toLocaleDateString('en-ZA')}
            </p>
          </div>
        </Card>

        <Card interactive={false}>
          <header className="border-b border-border px-6 py-3">
            <h3 className="text-sm font-semibold text-foreground">Quick stats</h3>
          </header>
          <dl className="grid grid-cols-2 gap-4 px-6 py-5 text-sm">
            <Stat
              label="MRR"
              value={loading ? '…' : formatZAR(mrr)}
              hint="Sum of active plans, normalised to monthly."
            />
            <Stat
              label="Hosting"
              value={loading ? '…' : `${links.length}`}
              hint="Active cPanel accounts."
            />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className="text-foreground">{value}</p>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-lg font-semibold text-foreground tabular-nums">
        {value}
      </dd>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
